// https://emailregex.com/
// Slightly modified to disallow quoted strings
export const EMAIL_REGEX =
  /^([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const cleanEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

/**
 * Cleans and validates an email to check if it is valid
 * @param origEmail the email to clean and validate
 */
export const validateEmail = (origEmail: string): string => {
  const email = cleanEmail(origEmail);
  if (!EMAIL_REGEX.test(email)) {
    throw new Error("Not a valid email");
  }
  return email;
};

/**
 * Remove portion of email alias including and after the "+" up until the "@".
 */
export const stripPlusComponent = (email: string): string => {
  const plusIndex = email.indexOf("+");
  const atIndex = email.lastIndexOf("@");
  return plusIndex >= 0 && plusIndex < atIndex
    ? `${email.slice(0, plusIndex)}${email.slice(atIndex)}`
    : email;
};

const escapeLikePattern = (value: string): string =>
  value.replace(/[%_\\]/g, "\\$&");

/**
 * Normalize an email for plus-alias-aware matching.
 *
 * Returns the cleaned email, the plus-stripped base email, and a LIKE pattern
 * (with ESCAPE '\') that matches any plus-aliased form of that base email.
 * Used to find deactivated plus-alias variants so `user+1@example.com` cannot
 * rejoin when `user@example.com` (or another alias) is deactivated.
 *
 * The alias pattern (`local+%@domain`) escapes LIKE wildcards and is intended
 * for org-scoped queries, where it is evaluated as a filter over a single
 * org's users.
 */
export const getEmailPlusAliasMatch = (
  email: string,
): {
  cleanedEmail: string;
  baseEmail: string;
  aliasLikePattern: string;
} => {
  const cleanedEmail = cleanEmail(email);
  const baseEmail = stripPlusComponent(cleanedEmail);
  const atIndex = baseEmail.lastIndexOf("@");
  const localPart = baseEmail.slice(0, atIndex);
  const domain = baseEmail.slice(atIndex + 1);
  return {
    cleanedEmail,
    baseEmail,
    aliasLikePattern: `${escapeLikePattern(localPart)}+%@${escapeLikePattern(domain)}`,
  };
};

export const SUPPORT_USER_EMAIL = "support@hex.tech";

export const isSupportUserEmail = (email: string): boolean =>
  stripPlusComponent(cleanEmail(email)) === SUPPORT_USER_EMAIL;

export const getEmailDomain = (email: string): string => {
  return email.split("@").pop() ?? "";
};

export const isEduDomain = (domain: string): boolean => {
  // EDU domains can end with .edu (US only) or .edu.[country_code] (e.g. "example.edu.au")
  // Additionally, .ac.[country_code] (e.g. "example.ac.uk") also can point to educational institutions
  // There are other country specific emails that do not follow this pattern so this is not an exhaustive check.
  // To check this, we'd need to include special cases (e.g. .some_university.ca) which this function does not currently do

  const eduRegexp = /\.edu(\.[a-z]{2})?$/;
  const acRegexp = /\.ac\.([a-z]{2})?$/; // @[*].ac points to the Ascension Island, and can be a first level domain

  return eduRegexp.test(domain) || acRegexp.test(domain);
};

// These emails are used for customers that buy us directly through 3rd party vendors.
// We should not allow those organizations to see or update their emails.
export const EMAILS_THAT_SHOULD_NOT_BE_UPDATED = [
  "ar+snowflake@hex.tech",
  "ar+aws@hex.tech",
];
